import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SEO from '../../components/SEO';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumb from '../../components/Breadcrumb';
import InternalLinks from '../../components/InternalLinks';
import { 
  ArrowRight,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Lightbulb,
  Euro,
  FileText,
  Users,
  Zap,
  ShieldCheck,
  Scale,
  Building2,
  Briefcase,
  TrendingUp
} from 'lucide-react';

export default function MAPADefinitionArticle() {
  return (
    <>
      <SEO 
        title="MAPA : Définition et Fonctionnement | Guide Complet 2026"
        description="Qu'est-ce qu'un MAPA (Marché à Procédure Adaptée) ? Seuils, fonctionnement, avantages. Guide complet pour comprendre et répondre aux MAPA en 2026."
        path="/blog/mapa-definition-fonctionnement"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b">
          <div className="container mx-auto px-4 py-3">
            <Breadcrumb 
              items={[
                { label: 'Blog', href: '/blog' },
                { label: 'MAPA : Définition et Fonctionnement' }
              ]}
            />
          </div>
        </div>

        {/* Article Header */}
        <article>
          <header className="bg-gradient-to-b from-violet-50 to-white py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                  Guide Procédures
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Calendar className="w-4 h-4" />
                  12 mars 2026
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  10 min de lecture
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                MAPA : Définition et Fonctionnement
              </h1>
              
              <p className="text-xl text-slate-600">
                Le MAPA (Marché à Procédure Adaptée) représente la majorité des marchés publics en France. Découvrez son fonctionnement, ses seuils et comment maximiser vos chances d'y répondre.
              </p>
            </div>
          </header>

          {/* Article Content */}
          <div className="container mx-auto px-4 max-w-4xl py-12">
            <div className="prose prose-lg max-w-none">
              
              <p>
                Si vous souhaitez vous lancer dans les marchés publics, le MAPA est souvent le meilleur point d'entrée. Plus souple que l'appel d'offres formalisé, il offre des opportunités accessibles, notamment pour les PME. Voici tout ce que vous devez savoir.
              </p>

              <p>
                Pour ne pas manquer les MAPA correspondant à votre activité, mettez en place une <Link to="/veille-marches-publics" className="text-violet-600 hover:text-violet-700 font-medium">veille marchés publics</Link> efficace. Notre <Link to="/plateforme-appels-offres" className="text-violet-600 hover:text-violet-700 font-medium">plateforme d'appels d'offres</Link> vous alerte automatiquement des nouvelles opportunités.
              </p>

              {/* Definition Card */}
              <Card className="my-8 border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-600" />
                    Définition du MAPA
                  </h3>
                  <p className="text-slate-700">
                    Le <strong>Marché à Procédure Adaptée (MAPA)</strong> est une procédure de passation des marchés publics dont les modalités sont librement fixées par l'acheteur en fonction de la nature et des caractéristiques du besoin, du nombre et de la localisation des opérateurs économiques susceptibles d'y répondre, ainsi que des circonstances de l'achat.
                  </p>
                  <p className="text-sm text-slate-500 mt-3">
                    Source : Article R2123-1 du Code de la commande publique
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Les seuils du MAPA en 2026
              </h2>

              <p>
                Le MAPA s'applique aux marchés dont le montant est inférieur aux seuils européens de procédure formalisée. Ces seuils sont révisés tous les deux ans.
              </p>

              {/* Thresholds Table */}
              <div className="overflow-x-auto my-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-violet-100">
                      <th className="border border-slate-300 px-4 py-3 text-left">Type d'acheteur</th>
                      <th className="border border-slate-300 px-4 py-3 text-left">Type de marché</th>
                      <th className="border border-slate-300 px-4 py-3 text-right">Seuil MAPA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2" rowSpan="2">
                        <div className="font-medium">État</div>
                        <div className="text-sm text-slate-500">Ministères, services déconcentrés</div>
                      </td>
                      <td className="border border-slate-300 px-4 py-2">Fournitures et services</td>
                      <td className="border border-slate-300 px-4 py-2 text-right font-semibold text-violet-700">143 000 € HT</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2">Travaux</td>
                      <td className="border border-slate-300 px-4 py-2 text-right font-semibold text-violet-700">5 538 000 € HT</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2" rowSpan="2">
                        <div className="font-medium">Collectivités territoriales</div>
                        <div className="text-sm text-slate-500">Communes, départements, régions</div>
                      </td>
                      <td className="border border-slate-300 px-4 py-2">Fournitures et services</td>
                      <td className="border border-slate-300 px-4 py-2 text-right font-semibold text-violet-700">221 000 € HT</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2">Travaux</td>
                      <td className="border border-slate-300 px-4 py-2 text-right font-semibold text-violet-700">5 538 000 € HT</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Card className="my-8 border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Bon à savoir
                  </h4>
                  <p className="text-amber-900">
                    En dessous de <strong>40 000 € HT</strong>, l'acheteur peut passer un marché sans publicité ni mise en concurrence préalable. C'est le <strong>marché de gré à gré</strong>. Au-delà et jusqu'aux seuils européens, le MAPA s'applique.
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                MAPA vs Appel d'offres : les différences
              </h2>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <Card className="border-2 border-violet-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                        <Zap className="w-6 h-6 text-violet-600" />
                      </div>
                      <h3 className="font-bold text-slate-900">MAPA</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Procédure souple et adaptable
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Délais de réponse courts (15-30 jours)
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Négociation possible
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Formalisme allégé
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Publicité adaptée au montant
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                        <Scale className="w-6 h-6 text-slate-600" />
                      </div>
                      <h3 className="font-bold text-slate-900">Appel d'offres</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        Procédure strictement encadrée
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        Délais longs (52 jours minimum)
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        Négociation interdite
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        Formalisme important
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        Publication obligatoire au JOUE
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Comment se déroule un MAPA ?
              </h2>

              <p>
                Bien que les modalités soient adaptables, un MAPA suit généralement les étapes suivantes :
              </p>

              <ol className="space-y-6 my-8">
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Publicité</strong>
                    <p className="text-slate-600 mt-1">
                      L'acheteur publie l'avis de marché sur son profil acheteur et/ou sur des plateformes spécialisées (BOAMP, presse régionale).
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Téléchargement du DCE</strong>
                    <p className="text-slate-600 mt-1">
                      Les entreprises intéressées téléchargent le <Link to="/blog/rc-vs-cctp-vs-ccap" className="text-violet-600 hover:text-violet-700 font-medium">Dossier de Consultation des Entreprises</Link> (RC, CCAP, CCTP...).
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">3</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Remise des offres</strong>
                    <p className="text-slate-600 mt-1">
                      Les candidats déposent leur offre avant la date limite, généralement de façon dématérialisée.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">4</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Analyse des offres</strong>
                    <p className="text-slate-600 mt-1">
                      L'acheteur évalue les offres selon les <Link to="/blog/criteres-attribution-marches-publics" className="text-violet-600 hover:text-violet-700 font-medium">critères d'attribution</Link> définis dans le RC.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">5</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Négociation (optionnelle)</strong>
                    <p className="text-slate-600 mt-1">
                      L'acheteur peut négocier avec les candidats les mieux classés pour améliorer les offres.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-lg">6</span>
                  <div>
                    <strong className="text-slate-900 text-lg">Attribution</strong>
                    <p className="text-slate-600 mt-1">
                      L'acheteur notifie le marché au titulaire retenu et informe les candidats non retenus.
                    </p>
                  </div>
                </li>
              </ol>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Pourquoi le MAPA est idéal pour les PME
              </h2>

              <div className="grid md:grid-cols-3 gap-4 my-8">
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-slate-900">Délais courts</h4>
                    <p className="text-sm text-slate-600 mt-1">15 à 30 jours pour répondre</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-slate-900">Moins de concurrence</h4>
                    <p className="text-sm text-slate-600 mt-1">Marchés locaux accessibles</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4 text-center">
                    <ShieldCheck className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-slate-900">Formalisme allégé</h4>
                    <p className="text-sm text-slate-600 mt-1">Moins de documents requis</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="my-8 border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-violet-800 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Statistique clé
                  </h4>
                  <p className="text-violet-900">
                    <strong>90% des marchés publics</strong> en France sont des MAPA. C'est donc la procédure la plus courante et celle qui offre le plus d'opportunités pour les entreprises.
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Conseils pour répondre à un MAPA
              </h2>

              <ul className="space-y-4 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-slate-900">Surveillez les publications</strong>
                    <p className="text-slate-600 text-sm mt-1">
                      Mettez en place une <Link to="/veille-marches-publics" className="text-violet-600 hover:text-violet-700 font-medium">veille marchés publics</Link> pour être alerté des nouveaux MAPA correspondant à votre activité.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-slate-900">Préparez vos documents types</strong>
                    <p className="text-slate-600 text-sm mt-1">
                      Ayez toujours à jour vos attestations, références, CV de l'équipe et documents administratifs.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-slate-900">Personnalisez votre réponse</strong>
                    <p className="text-slate-600 text-sm mt-1">
                      Même en MAPA, une réponse générique sera moins bien notée qu'une offre adaptée aux besoins spécifiques de l'acheteur.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-slate-900">Respectez les délais</strong>
                    <p className="text-slate-600 text-sm mt-1">
                      Une offre remise en retard, même d'une minute, est irrecevable. Anticipez les aléas techniques.
                    </p>
                  </div>
                </li>
              </ul>

              <Card className="my-8 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    Trouvez les MAPA qui vous correspondent
                  </h3>
                  <p className="mb-4 text-violet-100">
                    Notre IA analyse les MAPA publiés sur BOAMP, DECP et TED, et vous recommande les plus pertinents pour votre entreprise. Gagnez du temps dans votre veille.
                  </p>
                  <Link to="/register">
                    <Button className="bg-white text-violet-700 hover:bg-violet-50">
                      Démarrer ma veille gratuite
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Conclusion
              </h2>

              <p>
                Le MAPA est la porte d'entrée idéale dans les marchés publics pour les PME. Sa souplesse, ses délais raisonnables et son formalisme allégé en font une procédure accessible, à condition de bien maîtriser les fondamentaux.
              </p>

              <p>
                En résumé :
              </p>

              <ul className="space-y-2 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Le MAPA concerne les marchés sous les seuils européens</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>La négociation est possible (contrairement à l'appel d'offres)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>C'est 90% des marchés publics en France</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>La veille et la réactivité sont les clés du succès</span>
                </li>
              </ul>

            </div>

            {/* Aller plus loin - Pillar Pages */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Aller plus loin</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/veille-marches-publics" className="group p-4 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors">
                  <span className="font-semibold text-violet-700 group-hover:text-violet-800">Découvrir la veille marchés publics</span>
                  <p className="text-sm text-slate-600 mt-1">Surveillez les opportunités adaptées à votre entreprise</p>
                </Link>
                <Link to="/trouver-appels-offres" className="group p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                  <span className="font-semibold text-indigo-700 group-hover:text-indigo-800">Apprendre à trouver des appels d'offres</span>
                  <p className="text-sm text-slate-600 mt-1">Sources, méthodes et bonnes pratiques</p>
                </Link>
                <Link to="/analyse-dce" className="group p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                  <span className="font-semibold text-emerald-700 group-hover:text-emerald-800">Comprendre l'analyse d'un DCE</span>
                  <p className="text-sm text-slate-600 mt-1">Méthode IA pour extraire les informations clés</p>
                </Link>
                <Link to="/plateforme-appels-offres" className="group p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <span className="font-semibold text-orange-700 group-hover:text-orange-800">Découvrir la plateforme DCE Express</span>
                  <p className="text-sm text-slate-600 mt-1">Toutes les fonctionnalités pour répondre aux marchés</p>
                </Link>
              </div>
            </div>

            {/* Internal Links */}
            <InternalLinks 
              links={[
                { label: 'Comment trouver des appels d\'offres', href: '/blog/comment-trouver-appels-offres' },
                { label: 'Critères d\'attribution', href: '/blog/criteres-attribution-marches-publics' },
                { label: 'RC vs CCTP vs CCAP', href: '/blog/rc-vs-cctp-vs-ccap' },
                { label: 'Veille marchés publics', href: '/veille-marches-publics' }
              ]}
            />

            {/* Author */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">L'équipe Mon DCE Express</p>
                  <p className="text-sm text-slate-500">Experts en marchés publics et analyse DCE</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
